import React, {useRef, useState} from "react";
import {getGrowthProposals, decideGrowthProposal} from "@/lib/apiClient";
import {useData} from "@/lib/useData";
import {userError} from "@/lib/userError";
import {taskDeadline} from "@/lib/taskDates";
import GrowthDelivery from "./GrowthDelivery";

const buttonClass = "min-h-11 rounded-full border border-border px-4 py-2 text-sm font-medium disabled:opacity-40";
const hash = value => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const text = value => typeof value === "string" && value.trim().length > 0;
export function validGrowthProposal(row) {
  const p = row?.payload;
  return hash(row?.id) && hash(row?.fingerprint) && ["RECEIVED", "APPROVING"].includes(row?.state) &&
    p?.contractVersion === "acai-growth-task/0.1" && p.sourceSystem === "growth-director" &&
    p.visibility === "private" && text(p.subject) && text(p.goal) && text(p.successCriteria) &&
    text(p.priorityReason) && ["low", "normal", "high"].includes(p.priority) &&
    Number.isInteger(p.estimatedMinutes) && p.estimatedMinutes >= 1 && p.estimatedMinutes <= 480 &&
    p.timezone === "Europe/Helsinki" && (p.dueAt === null || text(p.dueAt) && Number.isFinite(Date.parse(p.dueAt)));
}

function Proposal({item, onDone}) {
  const lock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [finished, setFinished] = useState("");
  const p = item.payload;
  const decide = async decision => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setFailure("");
    try {
      const result = await decideGrowthProposal(item.id, decision, item.fingerprint);
      if (result?.proposal_id !== item.id || result?.status !== (decision === "approve" ? "APPROVED" : "REJECTED") ||
          decision === "approve" && (result.task_id !== `growth-${item.id}` || !["pending","sending","retry_wait","failed","blocked","delivered"].includes(result.callback)) ||
          decision === "reject" && (result.task_id !== null || result.callback !== "not_required")) {
        throw new Error("Tallennuksen kuittaus puuttuu.");
      }
      const message = decision === "approve" ? result.callback === "delivered" ? "Tehtävä lisättiin. Kasvujohtajan vastaanotto vahvistettiin." : "Tehtävä lisättiin. Tilapalautteen vastaanottoa ei ole vielä vahvistettu." : "Ehdotus hylättiin. Tehtävää ei luotu.";
      setFinished(message);
      await onDone(message);
    } catch (error) {
      setFailure(userError(error, "Tallennusta ei voitu vahvistaa. Yritä samaa päätöstä uudelleen."));
    } finally { lock.current = false; setBusy(false); }
  };
  return <article className="space-y-3 rounded-2xl border border-primary/25 bg-primary/5 p-4">
    <div><p className="text-xs text-muted-foreground">Kasvujohtajan tehtäväehdotus</p><h3 className="mt-1 font-semibold break-words">{p.subject}</h3></div>
    <p className="whitespace-pre-wrap break-words text-sm">{p.goal}</p>
    <p className="text-xs text-muted-foreground">{p.estimatedMinutes} min arvio · {{low:"matala",normal:"normaali",high:"korkea"}[p.priority]} tärkeys · {p.dueAt ? `määräaika ${taskDeadline(p.dueAt)}` : "ei määräaikaa"}</p>
    <details><summary className="min-h-11 cursor-pointer py-3 text-sm">Perustelu ja tavoiteltu tulos</summary>
      <p className="whitespace-pre-wrap break-words text-sm">{p.priorityReason}</p>
      <p className="mt-2 whitespace-pre-wrap break-words text-sm">{p.successCriteria}</p>
    </details>
    <p className="text-xs text-muted-foreground">Hyväksyntä lisää henkilökohtaisen tehtävän, ei kalenterivarausta. Kasvujohtajan vastaanotto vahvistetaan erikseen.</p>
    {item.state === "APPROVING" && <p role="status" className="text-sm text-amber-400">Tallennus jäi kesken. Jatka samaa hyväksyntää; toista tehtävää ei luoda.</p>}
    {failure && <p role="alert" className="text-sm text-destructive">{failure}</p>}
    {finished ? <p role="status" className="text-sm text-primary">{finished}</p> : <div className="flex flex-wrap gap-2">
      <button className={`${buttonClass} bg-primary text-primary-foreground`} disabled={busy} onClick={() => decide("approve")}>{busy ? "Tallennetaan…" : item.state === "APPROVING" ? "Jatka tallennusta" : "Hyväksy tehtävä"}</button>
      <button className={buttonClass} disabled={busy || item.state === "APPROVING"} onClick={() => decide("reject")}>Ei tehtävää</button>
    </div>}
  </article>;
}

export default function GrowthProposals() {
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState("");
  const state = useData(() => getGrowthProposals(offset), [offset], ["growth"], {autoRefreshMs:60000});
  if (state.loading) return <p role="status" className="text-sm text-muted-foreground">Tarkistetaan Kasvujohtajan ehdotuksia…</p>;
  if (state.error) return <div role="alert" className="text-sm">Kasvujohtajan ehdotuksia ei voitu hakea. <button className={buttonClass} onClick={state.refetch}>Yritä uudelleen</button></div>;
  if (state.data?.enabled === false) return <GrowthDelivery/>;
  if (state.data?.enabled !== true || !Array.isArray(state.data.items) || typeof state.data.has_more !== "boolean" ||
      !state.data.items.every(validGrowthProposal)) return <p role="alert">Ehdotusten tietoja ei voitu varmistaa. Hyväksyntä ei ole käytettävissä.</p>;
  const items = state.data.items;
  return <section className="space-y-3" aria-label="Kasvujohtajan tehtäväehdotukset">
    <h2 className="font-semibold">Kasvujohtajan ehdotukset</h2>
    {notice && <p role="status" className="text-sm text-primary">{notice}</p>}
    {!items.length && <p className="text-sm text-muted-foreground">Ei uusia tehtäväehdotuksia.</p>}
    {items.map(item => <Proposal key={`${item.id}:${item.fingerprint}`} item={item} onDone={async message => {setNotice(message); await state.refetch();}}/>)}
    <div className="flex flex-wrap gap-2">
      {offset > 0 && <button className={buttonClass} disabled={state.refreshing} onClick={() => setOffset(Math.max(0, offset - 20))}>Edelliset</button>}
      {state.data.has_more && <button className={buttonClass} disabled={state.refreshing} onClick={() => setOffset(offset + 20)}>Seuraavat ehdotukset</button>}
    </div>
    <GrowthDelivery/>
  </section>;
}
